import { useCallback, useState } from "react";

import { APP_CONSTANTS } from "@/config";

/**
 * The rail is 48px wide and carries seventeen icons, so a collapsed sidebar is
 * a real cost on a small screen and a real gain on a wide one. The clinic's
 * own choice wins; without one, a desktop starts open and anything narrower
 * than 1280px starts collapsed.
 */
export const SIDEBAR_OPEN_BREAKPOINT = "(min-width: 1280px)";

export function readSidebarPreference(): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(
      APP_CONSTANTS.SIDEBAR_OPEN_LOCAL_STORAGE_KEY
    );
    if (raw === "true") return true;
    if (raw === "false") return false;
    return null;
  } catch {
    return null;
  }
}

export function sidebarDefaultOpen(): boolean {
  const stored = readSidebarPreference();
  if (stored !== null) return stored;
  if (typeof window === "undefined" || !window.matchMedia) return true;
  try {
    return window.matchMedia(SIDEBAR_OPEN_BREAKPOINT).matches;
  } catch {
    return true;
  }
}

function persistSidebarPreference(open: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      APP_CONSTANTS.SIDEBAR_OPEN_LOCAL_STORAGE_KEY,
      String(open)
    );
  } catch {
    // A blocked storage only costs the clinic the remembered state.
  }
}

export function useSidebarPreference() {
  const [open, setOpen] = useState<boolean>(sidebarDefaultOpen);

  const setOpenAndPersist = useCallback((next: boolean) => {
    setOpen(next);
    persistSidebarPreference(next);
  }, []);

  return { open, setOpen: setOpenAndPersist };
}
